import { useTranslation } from 'react-i18next'
import { useContext } from 'react'
import {
  map,
  pipe,
  pathOr,
  set,
  lensPath,
  reject,
  propSatisfies,
  equals,
  __,
  values as rValues,
  flatten,
  filter,
  allPass,
  propEq,
  anyPass,
  complement,
  isEmpty
} from 'ramda'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Formik, Form } from 'formik'
import { colors, fontWeights } from '@/styles'
import Tooltip from '@/components/popper-tooltip'
import EditIcon from './edit-icon.svg?react'
import RemoveIcon from './remove-icon.svg?react'
import { show as showSelectRecipientFormModal } from './add-recipient-form-modal'
import { FieldInput, FieldDropdown } from '../inputs'
import Context from '../context'
import { getFormattedSigners, getFormattedPackageFiles } from '../utils'

const ContainerInnerDiv = styled.div`
  display: block;
  padding: 16px 16px 16px 26px;
`

const ContainerInnerNameContainer = styled.div`
  display: flex;
  justify-content: space-between;
`
const ContainerInnerName = styled.h5`
  display: block;
  text-align: left;
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  margin-bottom: 8px;

  img {
    margin-right: 10px;
  }
  width: 450px;
  max-width: 450px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FullText = styled.div`
  display: flex;
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  margin-bottom: 8px;

  img {
    margin-right: 10px;
  }
`

const ContainerInnerNameEditIcon = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: ${props => (props.disabled ? colors.grey64 : colors.blue48)};
  cursor: pointer;
  > :first-child {
    margin-right: 6px;
    ${props =>
      props.disabled &&
      `
    > path {
      fill: ${colors.grey64};
    }`}
  }
`
const ContainerInnerDeleteIcon = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: ${props => (props.disabled ? colors.grey64 : colors.red64)};
  cursor: pointer;
  > :first-child {
    margin-right: 6px;
    ${props =>
      props.disabled &&
      `
    > path {
      fill: ${colors.grey64};
    }`}
  }
`

const PrimaryFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: 394px 1fr;
  width: 100%;
  margin-bottom: 12px;
`

const PrimaryFieldsGridOne = styled.div`
  grid-column-start: 1;
`
const PrimaryFieldsGridTwo = styled.div`
  grid-column-start: 2;
  padding-left: 16px;
`

const AdjustedFieldInput = styled(FieldInput)`
  height: 48px;
`

const AdjustedFieldDropdown = styled(FieldDropdown)`
  height: 48px;
`

const EditingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  > :first-child {
    margin-right: 12px;
  }
`
const TooltipWrapper = styled.div`
  p {
    max-width: 300px;
    margin: 0;
    padding: 0;
  }
`

const StyledTooltip = styled(Tooltip)`
  > button {
    width: 100%;
  }
`

type User = {
  user_id: number
  first_name: string
  is_applicant: boolean
  last_name: string
  email: string
  recipient_signing_status?: string
  phone_number: string
  application_id: string
  dob: string
  contract_documents: []
  recipient_id: string
}

type FormValues = {
  email: string
  recipient_signing_status: string
}

const getContractdocuments = (signingStatus: string, user: User) => {
  const updatedDocuments = pipe(
    pathOr([], ['contract_documents']),
    map(set(lensPath(['recipient_signing_status']), signingStatus) as any)
  )(user)

  return updatedDocuments
}

const ContainerInner = ({
  contractId,
  user,
  role,
  showEdit,
  signees,
  setSignees,
  allowEdit,
  items
}: {
  contractId: string
  user: User
  role: string
  showEdit: boolean
  fileMetadata: any
  signees: any
  setSignees: any
  allowEdit: boolean
  items?: any
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { fileMetadata, setFileMetadata } = useContext(Context)

  const initialValues = {
    email: user.email || '',
    recipient_signing_status: pathOr(
      'signature_required',
      ['contract_documents', 0, 'recipient_signing_status'],
      user
    )
  }

  const hasItems = pipe(
    rValues,
    flatten as any,
    filter(
      allPass([
        propEq('recipient_id', user.recipient_id) as any,
        anyPass([propEq('inputType', 'signature'), propEq('inputType', 'initials')]) as any
      ])
    ),
    complement(isEmpty)
  )(items)

  const sentDocuments = pipe(
    pathOr([], ['package_files']),
    filter(propEq('state_machine', 'sent'))
  )(fileMetadata)
  const hasSentDocument = pipe(complement(isEmpty))(sentDocuments)

  const tooltipText = hasSentDocument
    ? t(
        'l.manage_recipients.has_sent_document.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, their status cannot be modified. Please void the contract and resend to modify.'
      )
    : t(
        'l.contract_manager.no_sent_document.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, their status cannot be modified. Please delete these fields to modify.'
      )

  const tooltipDeleteText = hasSentDocument
    ? t(
        'l.manage_recipients.has_sent_document.delete.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, they cannot be removed. Please void the contract and resend to modify.'
      )
    : t(
        'l.contract_manager.no_sent_document.delete.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, they cannot be removed. Please delete these fields to modify.'
      )

  const handleDelete = async () => {
    await dispatch.contract.removeSignerWithDocuments({
      signerId: user.recipient_id,
      contractId
    })

    // setFileMetadata to remove required signers from the documents
    if (fileMetadata && setFileMetadata) {
      pipe(
        pathOr([], ['signer']),
        map(reject(propSatisfies(equals(user.recipient_id), 'recipient_id'))),
        set(lensPath(['signer']), __, fileMetadata as any) as any,
        setFileMetadata
      )(fileMetadata)
    }

    // setSignees to update the signee list
    if (signees && setSignees)
      pipe(
        map(reject(propSatisfies(equals(user.recipient_id), 'recipient_id'))),
        setSignees
      )(signees)
    await dispatch.contract.getContractApplicants({ contractId: contractId })
  }
  const handleEdit = () =>
    showSelectRecipientFormModal({
      contractId,
      user,
      editing: true,
      items
    })

  const handleOnSubmit = async (
    values: any,
    { setSubmitting }: { setSubmitting: (param: boolean) => void }
  ) => {
    setSubmitting(true)

    await handleAddRecipients(values, user)
    setSubmitting(false)
  }

  const handleAddRecipients = async (values: FormValues, user: User) => {
    const signer = {
      email: values.email,
      phone_number: user.phone_number,
      first_name: user.first_name,
      last_name: user.last_name,
      dob: user.dob,
      application_id: user.application_id,
      user_id: user.user_id,
      contract_documents: getContractdocuments(values.recipient_signing_status, user)
    }

    await dispatch.contract.addRecipient({
      signer,
      contractId
    })

    const response = await dispatch.contract.getPackage({ contractId: contractId })
    const formattedSigners = getFormattedSigners(response?.body?.signers)
    const formattedFiles = getFormattedPackageFiles(response?.body?.package_files)

    setSignees(formattedSigners)
    setFileMetadata({
      ...fileMetadata,
      package_files: formattedFiles,
      signer: response?.body?.signers
    } as any)
  }

  const userName = `${user?.first_name} ${user?.last_name}`
  return (
    <ContainerInnerDiv>
      <ContainerInnerNameContainer>
        <Tooltip
          theme="secondary"
          enabled={true}
          size="medium"
          content={
            <TooltipWrapper>
              <FullText>{userName}</FullText>
            </TooltipWrapper>
          }
          placement={'top'}
          unfixedWidth
        >
          <ContainerInnerName>{userName}</ContainerInnerName>
        </Tooltip>

        <EditingContainer>
          {showEdit &&
            (allowEdit ? (
              <ContainerInnerNameEditIcon onClick={handleEdit}>
                <EditIcon />
                {t('common.edit', 'Edit')}
              </ContainerInnerNameEditIcon>
            ) : (
              <StyledTooltip
                enabled={true}
                size="medium"
                content={
                  <TooltipWrapper>
                    <FullText>
                      {t(
                        'l.contract_edit.add_recipients.disabled_edit.text',
                        'As this recipient was added from the applicant list, their information is automatically pulled from their application and cannot be modified.'
                      )}
                    </FullText>
                  </TooltipWrapper>
                }
                placement={'top'}
              >
                <ContainerInnerNameEditIcon disabled={true}>
                  <EditIcon />
                  {t('common.edit', 'Edit')}
                </ContainerInnerNameEditIcon>
              </StyledTooltip>
            ))}
          {role === 'renter' &&
            (!hasItems ? (
              <ContainerInnerDeleteIcon onClick={handleDelete}>
                <RemoveIcon />
                {t('common.remove', 'Remove')}
              </ContainerInnerDeleteIcon>
            ) : (
              <StyledTooltip
                enabled={true}
                size="medium"
                content={
                  <TooltipWrapper>
                    <FullText>{tooltipDeleteText}</FullText>
                  </TooltipWrapper>
                }
                placement={'top'}
              >
                <ContainerInnerDeleteIcon onClick={handleDelete} disabled>
                  <RemoveIcon />
                  {t('common.remove', 'Remove')}
                </ContainerInnerDeleteIcon>
              </StyledTooltip>
            ))}
        </EditingContainer>
      </ContainerInnerNameContainer>

      <Formik initialValues={initialValues} onSubmit={handleOnSubmit} enableReinitialize>
        {({ values, handleSubmit }) => {
          const handleChange = () => {
            handleSubmit()
          }
          return (
            <Form>
              <PrimaryFieldsContainer>
                <PrimaryFieldsGridOne>
                  <AdjustedFieldInput
                    disabled
                    name="email"
                    placeholder={t('l.contract_edit.add_recipients.email.title', 'E-mail')}
                  />
                </PrimaryFieldsGridOne>
                <PrimaryFieldsGridTwo>
                  {hasItems ? (
                    <StyledTooltip
                      enabled={true}
                      size="medium"
                      content={
                        <TooltipWrapper>
                          <FullText>{tooltipText}</FullText>
                        </TooltipWrapper>
                      }
                      placement={'top'}
                    >
                      <AdjustedFieldDropdown
                        id="recipient_signing_status"
                        name="recipient_signing_status"
                        placeholder={t(
                          'l.contract_edit.add_recipients.signing_status.title',
                          'Signing status'
                        )}
                        options={[
                          {
                            value: 'signature_required',
                            label: t('l.listing.edit.signature_required.text', 'Signature Required')
                          },
                          {
                            value: 'email_notification_required',
                            label: t(
                              'l.listing.edit.email_notification_required.text',
                              'Email a copy'
                            )
                          }
                        ]}
                        onChange={handleChange}
                        isDisabled={true}
                        fullWidth={true}
                      />
                    </StyledTooltip>
                  ) : (
                    <AdjustedFieldDropdown
                      id="recipient_signing_status"
                      name="recipient_signing_status"
                      placeholder={t(
                        'l.contract_edit.add_recipients.signing_status.title',
                        'Signing status'
                      )}
                      options={[
                        {
                          value: 'signature_required',
                          label: t('l.listing.edit.signature_required.text', 'Signature Required')
                        },
                        {
                          value: 'email_notification_required',
                          label: t(
                            'l.listing.edit.email_notification_required.text',
                            'Email a copy'
                          )
                        }
                      ]}
                      onChange={handleChange}
                    />
                  )}
                </PrimaryFieldsGridTwo>
              </PrimaryFieldsContainer>
            </Form>
          )
        }}
      </Formik>
    </ContainerInnerDiv>
  )
}

export default ContainerInner
